/**
 * Retention hooks UI — daily bonus, wheel of fortune, rewarded gems.
 */

import {
  RETENTION_DAILY_TRACK_LENGTH,
  RETENTION_GEMS_AD_COOLDOWN_MS,
  RETENTION_GEMS_AD_REWARD,
  RETENTION_WHEEL_FREE_SPIN_COOLDOWN_MS,
  RETENTION_WHEEL_SEGMENTS,
} from "../config/retention-config.js";
import { gameImageUrl } from "../assets/game-image-urls.js";
import { paintWheelFace, WHEEL_SEGMENT_TONES } from "../render/toon/overlays/wheel-face.js";
import {
  canOfferRewardedAds,
  requestRewardedAd as requestPortalRewardedAd,
  isRewardedAdGranted,
  isRewardedAdCancelled,
} from "./rewarded-ads-bridge.js";
import {
  REWARD_AD_NAME_EXTRA_GEMS,
  REWARD_AD_NAME_WHEEL_SPIN,
} from "../config/ad-placements.js";
import {
  canUseGemsAd,
  canUseWheelAdSpin,
  claimDailyBonus,
  formatGrantToast,
  getDailyClaimCooldownRemainingMs,
  getGrantRewardDisplayItems,
  buildWheelFaceConicGradient,
  getWheelSegmentDisplay,
  getWheelSegmentStartDeg,
  getCooldownRemainingMs,
  setCooldownBadgeLabel,
  getDailyRewardForStep,
  getDailyRewardTrackView,
  getDailyTilePresentation,
  getDefaultRetentionState,
  getWheelGrantForSegment,
  isDailyClaimAvailable,
  isFreeWheelSpinReady,
  markDailyAutoShown,
  markGemsAdUsed,
  markWheelSpinUsed,
  normalizeRetentionState,
  pickWheelSegmentIndex,
  shouldAutoOpenDailyBonus,
  shouldAutoOpenDailyBonusOnReturn,
  WHEEL_SPIN_ANIMATION_MS,
  WHEEL_SPIN_EASING,
  WHEEL_SPIN_FALLBACK_MS,
} from "./retention-hooks.js";
import { AnalyticsEvents } from "../analytics/event-names.js";
import { WHEEL_REWARD_PREVIEW_MS } from "./reward-reveal.js";
import { readStorageRecord, StorageSlot, writeStorageRecord } from "./storage.js";
import { resolveGameStorage } from "./persistence-backend.js";

var WHEEL_UI_FADE_MS = 450;

function afterOpacityTransition(el, done) {
  if (!el) {
    if (done) done();
    return;
  }
  var finished = false;
  var finish = function () {
    if (finished) return;
    finished = true;
    el.removeEventListener("transitionend", onEnd);
    if (done) done();
  };
  var onEnd = function (e) {
    if (e.target !== el || e.propertyName !== "opacity") return;
    finish();
  };
  el.addEventListener("transitionend", onEnd);
  setTimeout(finish, WHEEL_UI_FADE_MS + 40);
}

export function canOfferRetentionRewardedAds() {
  return canOfferRewardedAds();
}

/**
 * @param {{
 *   name?: string,
 *   onStarted?: () => void,
 *   onFinished?: () => void,
 *   onError?: (err: unknown) => void
 * }} options
 * @returns {Promise<{ granted: boolean, cancelled: boolean }>}
 */
export function requestRetentionRewardedAd(options) {
  return requestPortalRewardedAd(options || {});
}

/**
 * @param {import("./game.js").Game} game
 * @param {Record<string, HTMLElement | null | undefined>} dom
 */
export function createRetentionManager(game, dom) {
  return new RetentionManager(game, dom);
}

/**
 * @param {import("./game.js").Game} game
 * @param {Record<string, HTMLElement | null | undefined>} dom
 */
function RetentionManager(game, dom) {
  this.game = game;
  this.dom = dom;
  this.state = getDefaultRetentionState();
  this._tickRaf = 0;
  this._wheelSpinning = false;
  this._wheelAdPending = false;
  this._wheelIdleActive = false;
  this._wheelRotationDeg = 0;
  this._wheelPendingClaim = null;
  this._wheelRewardPreviewTimer = 0;
  this._playAgainPending = false;
  this._playAgainCallback = null;
  this._boundTick = this._tickPopups.bind(this);
  this._hudSyncTimer = 0;
  this._hudStageVisible = false;
  this._sessionReturnAutoOpenAttempted = false;
}

RetentionManager.prototype.load = function () {
  try {
    var parsed = readStorageRecord(this._storage(), StorageSlot.RETENTION);
    this.state = normalizeRetentionState(parsed);
  } catch (_) {
    this.state = getDefaultRetentionState();
  }
};

RetentionManager.prototype.save = function () {
  try {
    writeStorageRecord(this._storage(), StorageSlot.RETENTION, this.state);
  } catch (e) {
    console.warn("[Retention] save failed:", e);
  }
};

RetentionManager.prototype.reset = function () {
  this.state = getDefaultRetentionState();
  this._sessionReturnAutoOpenAttempted = false;
  this.save();
  this.syncHud();
  if (this.dom.dailyBonusGrid && this.dom.dailyBonusGrid.dataset.built === "1") {
    this._renderDailyGrid();
  }
  this._updateDailyClaimButton();
};

RetentionManager.prototype.init = function () {
  this.load();
  this._bindHandlers();
  this._buildDailyGrid();
  this._renderDailyGrid();
  this._buildWheel();
  this._syncGemsAdRewardDisplay();
  this.syncHud();
  this._syncAdButtonsVisibility();
};

RetentionManager.prototype._syncGemsAdRewardDisplay = function () {
  var amount = String(RETENTION_GEMS_AD_REWARD);
  if (this.dom.gemsAdRewardAmount) {
    this.dom.gemsAdRewardAmount.textContent = amount;
    this.dom.gemsAdRewardAmount.setAttribute("aria-label", amount + " gems");
  }
};

RetentionManager.prototype._storage = function () {
  return resolveGameStorage(null);
};

RetentionManager.prototype._hasCompletedARun = function () {
  return !!(this.game && this.game.hasCompletedARun);
};

RetentionManager.prototype._getPlayerDayIndex = function () {
  if (!this.game || !this.game.playerStats) return 0;
  return Math.max(0, Math.floor(Number(this.game.playerStats.dayIndex) || 0));
};

RetentionManager.prototype._bindHandlers = function () {
  var self = this;
  var pairs = [
    ["dailyBonusBtn", function () { self.openDailyBonus("hud"); }],
    ["wheelBtn", function () { self.openWheel(false); }],
    ["verticalDailyBonusBtn", function () { self.openDailyBonus("hud"); }],
    ["verticalWheelBtn", function () { self.openWheel(false); }],
    ["gemAdBtn", function () { self.openGemsAd(false); }],
    ["verticalGemAdBtn", function () { self.openGemsAd(false); }],
    ["dailyBonusCloseBtn", function () { self.closeDailyBonus(); }],
    ["dailyBonusClaimBtn", function () { self.claimDaily(); }],
    ["wheelSkipBtn", function () { self.closeWheel(); }],
    ["wheelFreeSpinBtn", function () { self.spinWheel("free"); }],
    ["wheelAdSpinBtn", function () { self.spinWheel("ad"); }],
    ["gemsAdSkipBtn", function () { self.closeGemsAd(); }],
    ["gemsAdWatchBtn", function () { self.watchGemsAd(); }],
  ];
  for (var i = 0; i < pairs.length; i++) {
    var el = this.dom[pairs[i][0]];
    if (el && !el.dataset.retentionBound) {
      el.dataset.retentionBound = "1";
      el.addEventListener("click", pairs[i][1]);
    }
  }
};

RetentionManager.prototype.syncHud = function () {
  var now = Date.now();
  var dailyReady = isDailyClaimAvailable(this.state, now);
  var wheelReady = isFreeWheelSpinReady(this.state, now);
  this._setDot(this.dom.dailyBonusBtn, dailyReady);
  this._setDot(this.dom.wheelBtn, wheelReady);
  this._setDot(this.dom.verticalDailyBonusBtn, dailyReady);
  this._setDot(this.dom.verticalWheelBtn, wheelReady);
  this._syncPokiVisibility(now);
  if (this._hudStageVisible) this._scheduleHudSync(now);
};

RetentionManager.prototype._getNextHudSyncDelayMs = function (nowMs) {
  var now = nowMs != null ? nowMs : Date.now();
  var delays = [];
  if (!isFreeWheelSpinReady(this.state, now)) {
    var wheelRemain = getCooldownRemainingMs(
      this.state.wheelLastFreeSpinAt,
      RETENTION_WHEEL_FREE_SPIN_COOLDOWN_MS,
      now
    );
    if (wheelRemain > 0) delays.push(wheelRemain);
  }
  if (!isDailyClaimAvailable(this.state, now)) {
    var dailyRemain = getDailyClaimCooldownRemainingMs(this.state, now);
    if (dailyRemain > 0) delays.push(dailyRemain);
  }
  if (!delays.length) return null;
  return Math.min.apply(null, delays) + 50;
};

RetentionManager.prototype._scheduleHudSync = function (nowMs) {
  this._clearHudSyncTimer();
  var delayMs = this._getNextHudSyncDelayMs(nowMs);
  if (delayMs == null) return;
  var self = this;
  this._hudSyncTimer = setTimeout(function () {
    self._hudSyncTimer = 0;
    self.syncHud();
  }, delayMs);
};

RetentionManager.prototype._clearHudSyncTimer = function () {
  if (!this._hudSyncTimer) return;
  clearTimeout(this._hudSyncTimer);
  this._hudSyncTimer = 0;
};

RetentionManager.prototype.syncStageVisibility = function (visible) {
  var show = !!visible;
  this._hudStageVisible = show;
  var showAds = canOfferRetentionRewardedAds();
  var ids = [
    "dailyBonusBtn", "wheelBtn",
    "verticalDailyBonusBtn", "verticalWheelBtn",
  ];
  for (var i = 0; i < ids.length; i++) {
    var el = this.dom[ids[i]];
    if (el) el.classList.toggle("hidden", !show);
  }
  if (this.dom.gemAdBtn) this.dom.gemAdBtn.classList.toggle("hidden", !show || !showAds);
  if (this.dom.verticalGemAdBtn) {
    this.dom.verticalGemAdBtn.classList.toggle("hidden", !show || !showAds);
  }
  if (show) {
    this.syncHud();
    this.maybeAutoOpenDailyOnReturn();
  } else {
    this._clearHudSyncTimer();
  }
};

RetentionManager.prototype._syncAdButtonsVisibility = function () {
  var show = canOfferRetentionRewardedAds();
  if (this.dom.wheelAdSpinBtn) this.dom.wheelAdSpinBtn.classList.toggle("hidden", !show);
  if (this.dom.gemAdBtn) this.dom.gemAdBtn.classList.toggle("hidden", !show);
  if (this.dom.verticalGemAdBtn) this.dom.verticalGemAdBtn.classList.toggle("hidden", !show);
  if (this.dom.gemsAdOverlay) this.dom.gemsAdOverlay.classList.toggle("retention--ads-disabled", !show);
  if (this.dom.wheelOverlay) this.dom.wheelOverlay.classList.toggle("retention--ads-disabled", !show);
};

RetentionManager.prototype._setDot = function (btn, visible) {
  if (!btn) return;
  var badge = btn.querySelector(".retention-icon-btn__badge");
  if (!badge) return;
  badge.classList.toggle("hidden", !visible);
  if (typeof badge.setAttribute === "function") {
    badge.setAttribute("aria-hidden", visible ? "false" : "true");
  }
};

RetentionManager.prototype._trackRetentionAnalytics = function (eventName, extraParams) {
  if (!this.game || typeof this.game._trackAnalytics !== "function") return;
  this.game._trackAnalytics(eventName, extraParams || {});
};

RetentionManager.prototype._trackPokiAction = function (name, params) {
  if (this.game && typeof this.game._trackPokiEvent === "function") {
    this.game._trackPokiEvent(name, params);
  }
};

RetentionManager.prototype._syncPokiVisibility = function (nowMs) {
  if (!this.game || typeof this.game._getPokiEngagement !== "function"
    || !this.game._getPokiEngagement()) return;
  if (typeof document !== "undefined" && document.hidden) return;
  var now = nowMs != null ? nowMs : Date.now();
  var freeReady = isFreeWheelSpinReady(this.state, now);
  var dailyReady = isDailyClaimAvailable(this.state, now) && !this._isRewardRevealOpen();
  var adReady = canOfferRetentionRewardedAds();
  var wheelOpen = this._isOpen(this.dom.wheelOverlay) && !this._wheelActionLocked();
  var dailyOpen = this._isOpen(this.dom.dailyBonusOverlay);
  var hudVisible = this._hudStageVisible && !this._anyPopupOpen()
    && !this.game.isDialogPaused && !this.game.isReviveOfferActive;
  var key = [this.game.currentCycleIndex, hudVisible, freeReady, dailyReady,
    adReady, wheelOpen, dailyOpen].join(":");
  if (key === this._pokiVisibilityKey) return;
  var visible = function (el) {
    return !!(el && !el.closest(".hidden") && el.getClientRects().length);
  };
  var view = {
    wheelButton: hudVisible && (visible(this.dom.wheelBtn) || visible(this.dom.verticalWheelBtn)),
    dailyButton: hudVisible && (visible(this.dom.dailyBonusBtn) || visible(this.dom.verticalDailyBonusBtn)),
    freeReady: freeReady,
    freeSpin: wheelOpen && freeReady && visible(this.dom.wheelFreeSpinBtn),
    adSpin: wheelOpen && adReady && visible(this.dom.wheelAdSpinBtn),
    dailyClaim: dailyOpen && dailyReady && visible(this.dom.dailyBonusClaimBtn),
  };
  this.game._trackPokiRetentionVisibility(view);
  // Retry after first layout if no UI has actually appeared yet.
  if (view.wheelButton || view.dailyButton || view.freeSpin || view.adSpin || view.dailyClaim) {
    this._pokiVisibilityKey = key;
  }
};

RetentionManager.prototype._pauseForOverlay = function () {
  if (this.game && typeof this.game._pauseForDialog === "function") {
    this.game._pauseForDialog();
  }
};

RetentionManager.prototype._resumeFromOverlay = function () {
  if (this.game && typeof this.game._resumeFromDialog === "function") {
    this.game._resumeFromDialog();
  }
  this._syncPokiVisibility();
};

RetentionManager.prototype._showOverlay = function (el) {
  if (!el) return;
  if (this.game && typeof this.game._showOverlayWithFade === "function") {
    this.game._showOverlayWithFade(el);
  } else {
    el.classList.remove("hidden");
  }
};

RetentionManager.prototype._hideOverlay = function (el, cb) {
  if (!el) {
    if (cb) cb();
    return;
  }
  if (this.game && typeof this.game._hideOverlayWithFade === "function") {
    this.game._hideOverlayWithFade(el, cb);
  } else {
    el.classList.add("hidden");
    if (cb) cb();
  }
};

RetentionManager.prototype._toast = function (msg) {
  if (this.game && typeof this.game.showToast === "function") {
    this.game.showToast(msg);
  }
};

RetentionManager.prototype._isRewardRevealOpen = function () {
  return !!(this.game && this.game.rewardReveal && this.game.rewardReveal.isOpen());
};

RetentionManager.prototype._openRewardReveal = function (grant, onClaim, onClosed, opts) {
  if (this.game && typeof this.game._openRewardReveal === "function") {
    this.game._openRewardReveal(grant, onClaim, onClosed, opts);
    return;
  }
  if (typeof onClaim === "function") onClaim();
};

RetentionManager.prototype._playWheelSpinSound = function () {
  if (this.game && this.game.soundManager && typeof this.game.soundManager.play === "function") {
    this.game.soundManager.play("wheel_spin");
  }
};

RetentionManager.prototype._applyGrant = function (grant) {
  if (!this.game || !grant) return;
  if (grant.gems) this.game.gemsCollected = Math.max(0, (Number(this.game.gemsCollected) || 0) + grant.gems);
  if (grant.shakes) this.game.shakeUses = Math.max(0, (Number(this.game.shakeUses) || 0) + grant.shakes);
  if (grant.clears) {
    this.game.bombTripleUses = Math.max(0, (Number(this.game.bombTripleUses) || 0) + grant.clears);
  }
  if (grant.shrinks) this.game.minimizeUses = Math.max(0, (Number(this.game.minimizeUses) || 0) + grant.shrinks);
  if (typeof this.game.updateGlobalGemDisplay === "function") this.game.updateGlobalGemDisplay();
  if (typeof this.game.updateShakeUI === "function") this.game.updateShakeUI();
  if (typeof this.game._persistLifetimeEconomy === "function") this.game._persistLifetimeEconomy();
};

RetentionManager.prototype._startPopupTick = function () {
  if (this._tickRaf) return;
  var self = this;
  var loop = function () {
    self._tickPopups();
    self._tickRaf = requestAnimationFrame(loop);
  };
  this._tickRaf = requestAnimationFrame(loop);
};

RetentionManager.prototype._stopPopupTick = function () {
  if (!this._tickRaf) return;
  cancelAnimationFrame(this._tickRaf);
  this._tickRaf = 0;
};

RetentionManager.prototype.isAnyPopupOpen = function () {
  return this._anyPopupOpen();
};

RetentionManager.prototype._anyPopupOpen = function () {
  return this._isOpen(this.dom.dailyBonusOverlay)
    || this._isOpen(this.dom.wheelOverlay)
    || this._isOpen(this.dom.gemsAdOverlay)
    || this._isRewardRevealOpen();
};

RetentionManager.prototype._isOpen = function (el) {
  return !!(el && !el.classList.contains("hidden"));
};

RetentionManager.prototype._tickPopups = function () {
  if (!this._anyPopupOpen()) {
    this._stopPopupTick();
    return;
  }
  var now = Date.now();
  if (this._isOpen(this.dom.wheelOverlay)) {
    this._updateWheelButtons(now);
    this._tickWheelIdle();
  }
  if (this._isOpen(this.dom.gemsAdOverlay)) this._updateGemsAdButton(now);
  if (this._isOpen(this.dom.dailyBonusOverlay)) this._updateDailyClaimButton(now);
};

RetentionManager.prototype.requestPlayAgain = function (callback) {
  if (this._playAgainPending) return;
  if (!shouldAutoOpenDailyBonus(this.state, Date.now(), this._hasCompletedARun())) {
    if (callback) callback();
    return;
  }
  this.state = markDailyAutoShown(this.state, Date.now());
  this.save();
  this._playAgainPending = true;
  this._playAgainCallback = callback || null;
  this.openDailyBonus("play_again");
};

RetentionManager.prototype._hideGameOverOverlay = function () {
  if (!this.game) return;
  var dom = this.game.dom;
  if (dom && dom.gameOverOverlay) {
    dom.gameOverOverlay.classList.add("hidden");
  }
  if (typeof this.game._hideLeaderboardCard === "function") {
    this.game._hideLeaderboardCard();
  }
};

RetentionManager.prototype._afterDailyOverlayHidden = function () {
  this._resumeFromOverlay();
  this._finishPlayAgainIfNeeded();
};

RetentionManager.prototype.maybeAutoOpenDailyOnReturn = function () {
  if (this._sessionReturnAutoOpenAttempted) return;
  this._sessionReturnAutoOpenAttempted = true;
  if (this._anyPopupOpen()) return;
  var now = Date.now();
  if (!shouldAutoOpenDailyBonusOnReturn(
    this.state,
    now,
    this._hasCompletedARun(),
    this._getPlayerDayIndex()
  )) {
    return;
  }
  this.state = markDailyAutoShown(this.state, now);
  this.save();
  this.openDailyBonus("return_visit");
};

RetentionManager.prototype._finishPlayAgainIfNeeded = function () {
  if (!this._playAgainPending) return;
  this._playAgainPending = false;
  var cb = this._playAgainCallback;
  this._playAgainCallback = null;
  if (cb) cb();
};

RetentionManager.prototype.openDailyBonus = function (openSource) {
  openSource = openSource || "hud";
  if (openSource === "play_again") {
    this._hideGameOverOverlay();
  }
  this._pauseForOverlay();
  this._renderDailyGrid();
  this._updateDailyClaimButton();
  this._showOverlay(this.dom.dailyBonusOverlay);
  this._syncPokiVisibility();
  this._startPopupTick();
  this._trackRetentionAnalytics(AnalyticsEvents.DAILY_OPENED, {
    open_source: openSource,
  });
};

RetentionManager.prototype.closeDailyBonus = function () {
  var self = this;
  this._hideOverlay(this.dom.dailyBonusOverlay, function () {
    self._afterDailyOverlayHidden();
  });
};

RetentionManager.prototype.claimDaily = function () {
  if (!isDailyClaimAvailable(this.state, Date.now())) return;
  if (this._isRewardRevealOpen()) return;
  var previewGrant = getDailyRewardForStep(this.state.dailyBonusStep);
  var claimedStep = this.state.dailyBonusStep;
  var self = this;
  this._openRewardReveal(previewGrant, function () {
    self._trackRetentionAnalytics(AnalyticsEvents.DAILY_CLAIMED, {
      daily_step: claimedStep,
    });
    var result = claimDailyBonus(self.state, Date.now());
    self.state = result.state;
    self.save();
    self._applyGrant(result.grant);
    self.syncHud();
    self._renderDailyGrid();
    self._updateDailyClaimButton();
    if (self._playAgainPending) {
      self._hideOverlay(self.dom.dailyBonusOverlay, function () {
        self._afterDailyOverlayHidden();
      });
    }
  }, null);
};

RetentionManager.prototype._updateDailyClaimButton = function (nowMs) {
  var btn = this.dom.dailyBonusClaimBtn;
  if (!btn) return;
  var now = nowMs != null ? nowMs : Date.now();
  var available = isDailyClaimAvailable(this.state, now) && !this._isRewardRevealOpen();
  var remain = getDailyClaimCooldownRemainingMs(this.state, now);
  btn.disabled = !available;
  this._syncPokiVisibility(now);
  var label = this.dom.dailyBonusClaimLabel;
  if (label) {
    label.textContent = "Claim";
  } else if (btn.childElementCount === 0) {
    btn.textContent = "Claim";
  }
  var badge = this.dom.dailyBonusClaimCooldown;
  if (badge) {
    var showBadge = remain > 0;
    setCooldownBadgeLabel(badge, remain);
    badge.classList.toggle("hidden", !showBadge);
    badge.setAttribute("aria-hidden", showBadge ? "false" : "true");
  }
};

RetentionManager.prototype._buildDailyGrid = function () {
  var mount = this.dom.dailyBonusGrid;
  if (!mount || mount.dataset.built === "1") return;
  mount.dataset.built = "1";
  mount.innerHTML = "";
  for (var step = 1; step <= RETENTION_DAILY_TRACK_LENGTH; step++) {
    var tile = document.createElement("div");
    tile.className = "retention-daily-tile" + (step === 7 ? " retention-daily-tile--chest" : "");
    tile.dataset.step = String(step);
    var label = document.createElement("span");
    label.className = "retention-daily-tile__label";
    label.textContent = step === 7 ? "Day 7" : "Day " + step;
    var reward = document.createElement("div");
    if (step === 7) {
      var grant7 = getDailyRewardForStep(step);
      reward.className = "retention-daily-tile__reward retention-daily-tile__reward--chest";
      reward.setAttribute("aria-label", formatGrantToast(grant7));
      var chestIcon = document.createElement("img");
      chestIcon.className = "retention-daily-tile__reward-icon retention-daily-tile__chest-visual";
      chestIcon.alt = "";
      chestIcon.setAttribute("aria-hidden", "true");
      chestIcon.setAttribute("data-game-img", "chest");
      var chestUrl = gameImageUrl("chest");
      if (chestUrl) chestIcon.src = chestUrl;
      var chestTitle = document.createElement("span");
      chestTitle.className = "retention-daily-tile__chest-title";
      chestTitle.textContent = "Special reward";
      reward.appendChild(chestTitle);
      reward.appendChild(chestIcon);
    } else {
      reward.className = "retention-daily-tile__reward";
      var grant = getDailyRewardForStep(step);
      var items = getGrantRewardDisplayItems(grant);
      reward.setAttribute("aria-label", formatGrantToast(grant));
      if (items.length) {
        var stack = document.createElement("div");
        stack.className = "retention-daily-tile__reward-stack";
        var icon = document.createElement("img");
        icon.className = "retention-daily-tile__reward-icon";
        icon.alt = "";
        icon.setAttribute("aria-hidden", "true");
        icon.setAttribute("data-game-img", items[0].icon);
        var iconUrl = gameImageUrl(items[0].icon);
        if (iconUrl) icon.src = iconUrl;
        var amount = document.createElement("span");
        amount.className = "retention-daily-tile__reward-amount";
        amount.textContent = items[0].amount;
        stack.appendChild(icon);
        stack.appendChild(amount);
        reward.appendChild(stack);
      }
    }
    tile.appendChild(label);
    tile.appendChild(reward);
    mount.appendChild(tile);
  }
};

RetentionManager.prototype._renderDailyGrid = function () {
  var mount = this.dom.dailyBonusGrid;
  if (!mount) return;
  var trackView = getDailyRewardTrackView(this.state, Date.now());
  var tiles = mount.querySelectorAll(".retention-daily-tile");
  for (var i = 0; i < tiles.length; i++) {
    var tile = tiles[i];
    var step = Math.floor(Number(tile.dataset.step) || 0);
    var presentation = getDailyTilePresentation(step, trackView);
    tile.classList.remove(
      "retention-daily-tile--claimed",
      "retention-daily-tile--current",
      "retention-daily-tile--locked"
    );
    tile.classList.add("retention-daily-tile--" + presentation.status);
    var labelEl = tile.querySelector(".retention-daily-tile__label");
    if (labelEl) {
      labelEl.textContent = presentation.label;
    }
  }
};

RetentionManager.prototype._buildWheel = function () {
  var disc = this.dom.wheelDisc;
  var rotator = this.dom.wheelRotator;
  var labelsMount = this.dom.wheelLabels;
  if (!disc || !rotator || disc.dataset.built === "1") return;
  disc.dataset.built = "1";

  var face = rotator.querySelector(".retention-wheel-disc__face");
  if (!face) return;

  var segmentAngle = 360 / RETENTION_WHEEL_SEGMENTS.length;
  // Flat segments in the cartoon tones until (or without) the drawn face canvas.
  face.style.background = buildWheelFaceConicGradient(
    RETENTION_WHEEL_SEGMENTS.length,
    WHEEL_SEGMENT_TONES.map(function (tone) { return tone.base; })
  );
  paintWheelFace(face, RETENTION_WHEEL_SEGMENTS.length);

  if (labelsMount) {
    labelsMount.innerHTML = "";
    for (var j = 0; j < RETENTION_WHEEL_SEGMENTS.length; j++) {
      var seg = RETENTION_WHEEL_SEGMENTS[j];
      var display = getWheelSegmentDisplay(seg);
      var labelEl = document.createElement("div");
      labelEl.className = "retention-wheel-label";
      labelEl.dataset.segmentIndex = String(j);
      labelEl.style.setProperty("--wheel-label-angle", (j * segmentAngle + segmentAngle / 2) + "deg");
      var stack = document.createElement("div");
      stack.className = "retention-wheel-label__stack";
      var icon = document.createElement("img");
      icon.className = "retention-wheel-label__icon";
      icon.alt = "";
      icon.setAttribute("aria-hidden", "true");
      icon.setAttribute("data-game-img", display.icon);
      var iconUrl = gameImageUrl(display.icon);
      if (iconUrl) icon.src = iconUrl;
      var amount = document.createElement("span");
      amount.className = "retention-wheel-label__amount";
      amount.textContent = display.amount;
      stack.appendChild(icon);
      stack.appendChild(amount);
      labelEl.appendChild(stack);
      labelsMount.appendChild(labelEl);
    }
  }

  rotator.style.transform = "rotate(0deg)";
  this._wheelRotationDeg = 0;
};

RetentionManager.prototype._startWheelIdle = function () {
  this._wheelIdleActive = true;
};

RetentionManager.prototype._stopWheelIdle = function () {
  this._wheelIdleActive = false;
};

RetentionManager.prototype._tickWheelIdle = function () {
  if (!this._wheelIdleActive || this._wheelSpinning || this._wheelPendingClaim) return;
  var rotator = this.dom.wheelRotator;
  if (!rotator) return;
  this._wheelRotationDeg = (this._wheelRotationDeg + 0.12) % 360;
  rotator.style.transition = "none";
  rotator.style.transform = "rotate(" + this._wheelRotationDeg + "deg)";
};

RetentionManager.prototype._clearWheelRewardPreviewTimer = function () {
  if (!this._wheelRewardPreviewTimer) return;
  clearTimeout(this._wheelRewardPreviewTimer);
  this._wheelRewardPreviewTimer = 0;
};

RetentionManager.prototype._wheelActionLocked = function () {
  return this._wheelSpinning
    || this._wheelAdPending
    || !!this._wheelPendingClaim
    || !!this._wheelRewardPreviewTimer
    || this._isRewardRevealOpen();
};

RetentionManager.prototype._getWheelSegmentOverlays = function () {
  return this.dom.wheelSegmentDim ? [this.dom.wheelSegmentDim] : [];
};

RetentionManager.prototype._clearWheelWinnerHighlight = function (opts, done) {
  if (typeof opts === "function") {
    done = opts;
    opts = null;
  }
  opts = opts || {};
  var labelsMount = this.dom.wheelLabels;
  if (labelsMount && !opts.skipLabelCleanup) {
    var winnerLabels = labelsMount.querySelectorAll(".retention-wheel-label--winner");
    for (var i = 0; i < winnerLabels.length; i++) {
      winnerLabels[i].classList.remove("retention-wheel-label--winner");
    }
    var claimingLabels = labelsMount.querySelectorAll(".retention-wheel-label--claiming");
    for (var c = 0; c < claimingLabels.length; c++) {
      claimingLabels[c].classList.remove("retention-wheel-label--claiming");
    }
  }
  var overlays = this._getWheelSegmentOverlays();
  if (!overlays.length) {
    if (done) done();
    return;
  }
  var hadActive = false;
  for (var o = 0; o < overlays.length; o++) {
    if (overlays[o].classList.contains("retention-wheel-disc__segment-overlay--active")) {
      hadActive = true;
      break;
    }
  }
  for (var r = 0; r < overlays.length; r++) {
    overlays[r].classList.remove("retention-wheel-disc__segment-overlay--active");
  }
  if (!hadActive || opts.instant) {
    for (var h = 0; h < overlays.length; h++) {
      overlays[h].classList.add("hidden");
    }
    if (done) done();
    return;
  }
  afterOpacityTransition(overlays[0], function () {
    for (var i = 0; i < overlays.length; i++) {
      if (!overlays[i].classList.contains("retention-wheel-disc__segment-overlay--active")) {
        overlays[i].classList.add("hidden");
      }
    }
    if (done) done();
  });
};

RetentionManager.prototype._setWheelWinnerHighlight = function (segmentIndex) {
  var labelsMount = this.dom.wheelLabels;
  if (labelsMount) {
    var winnerLabels = labelsMount.querySelectorAll(".retention-wheel-label--winner");
    for (var i = 0; i < winnerLabels.length; i++) {
      winnerLabels[i].classList.remove("retention-wheel-label--winner");
    }
    var label = labelsMount.querySelector('[data-segment-index="' + segmentIndex + '"]');
    if (label) label.classList.add("retention-wheel-label--winner");
  }
  var overlays = this._getWheelSegmentOverlays();
  if (!overlays.length) return;
  var segmentAngle = 360 / RETENTION_WHEEL_SEGMENTS.length;
  var startDeg = getWheelSegmentStartDeg(segmentIndex) + "deg";
  var angleDeg = segmentAngle + "deg";
  for (var j = 0; j < overlays.length; j++) {
    var overlay = overlays[j];
    overlay.style.setProperty("--wheel-shine-start", startDeg);
    overlay.style.setProperty("--wheel-segment-angle", angleDeg);
    overlay.classList.remove("hidden");
    if (typeof overlay.offsetWidth === "number") {
      void overlay.offsetWidth;
    }
    overlay.classList.add("retention-wheel-disc__segment-overlay--active");
  }
};

RetentionManager.prototype._finishWheelRewardReveal = function (pending, onComplete) {
  var self = this;
  this._openRewardReveal(pending.grant, function () {
    self._trackPokiAction("wheel_reward_claimed");
    self._applyGrant(pending.grant);
    self.syncHud();
    self._wheelPendingClaim = null;
    self._clearWheelWinnerHighlight({ instant: true }, function () {
      self._wheelSpinning = false;
      self._startWheelIdle();
      self._updateWheelButtons(Date.now());
      if (onComplete) onComplete();
    });
  });
};

RetentionManager.prototype._scheduleWheelRewardReveal = function (pending) {
  var self = this;
  this._clearWheelRewardPreviewTimer();
  this._wheelRewardPreviewTimer = setTimeout(function () {
    self._wheelRewardPreviewTimer = 0;
    if (!self._wheelPendingClaim) return;
    self._finishWheelRewardReveal(self._wheelPendingClaim);
  }, WHEEL_REWARD_PREVIEW_MS);
};

RetentionManager.prototype._resetWheelRewardFlow = function () {
  this._clearWheelRewardPreviewTimer();
  this._wheelPendingClaim = null;
  this._clearWheelWinnerHighlight({ instant: true });
};

RetentionManager.prototype.claimWheelReward = function (onComplete) {
  if (!this._wheelPendingClaim) {
    if (onComplete) onComplete();
    return;
  }
  this._clearWheelRewardPreviewTimer();
  this._finishWheelRewardReveal(this._wheelPendingClaim, onComplete);
};

RetentionManager.prototype._showWheelOverlay = function () {
  this._showOverlay(this.dom.wheelOverlay);
};

RetentionManager.prototype._hideWheelOverlay = function (cb) {
  this._hideOverlay(this.dom.wheelOverlay, cb);
};

RetentionManager.prototype.openWheel = function () {
  var self = this;
  this._resetWheelRewardFlow();
  this._wheelSpinning = false;
  this._wheelSpunSinceOpen = false;
  this._wheelOpenedForAnalytics = true;
  this._pauseForOverlay();
  this._updateWheelButtons(Date.now());
  this._startWheelIdle();
  this._showWheelOverlay();
  this._syncPokiVisibility();
  this._startPopupTick();
  this._trackRetentionAnalytics(AnalyticsEvents.WHEEL_OPENED, {
    free_ready: isFreeWheelSpinReady(this.state, Date.now()) ? 1 : 0,
  });
};

RetentionManager.prototype.closeWheel = function () {
  if (this._wheelActionLocked()) return;
  if (this._wheelOpenedForAnalytics && !this._wheelSpunSinceOpen) {
    this._trackPokiAction("wheel_closed_without_spin");
  }
  this._wheelOpenedForAnalytics = false;
  var self = this;
  this._resetWheelRewardFlow();
  this._wheelSpinning = false;
  this._stopWheelIdle();
  this._hideWheelOverlay(function () {
    self._resumeFromOverlay();
  });
};

RetentionManager.prototype._updateWheelButtons = function (now) {
  this._syncPokiVisibility(now);
  var freeBtn = this.dom.wheelFreeSpinBtn;
  var freeLabel = this.dom.wheelFreeSpinLabel;
  var adBtn = this.dom.wheelAdSpinBtn;
  var freeBadge = this.dom.wheelFreeSpinCooldown;
  var adBadge = this.dom.wheelAdSpinCooldown;
  var skipBtn = this.dom.wheelSkipBtn;
  var locked = this._wheelActionLocked();

  if (freeBtn) {
    var freeReady = isFreeWheelSpinReady(this.state, now);
    var freeRemain = getCooldownRemainingMs(
      this.state.wheelLastFreeSpinAt,
      RETENTION_WHEEL_FREE_SPIN_COOLDOWN_MS,
      now
    );
    freeBtn.disabled = locked || !freeReady;
    if (freeLabel) freeLabel.textContent = "Free spin";
    if (freeBadge) {
      setCooldownBadgeLabel(freeBadge, freeRemain);
      freeBadge.classList.toggle("hidden", freeReady);
      freeBadge.setAttribute("aria-hidden", freeReady ? "true" : "false");
    }
  }
  if (adBtn && !adBtn.classList.contains("hidden")) {
    adBtn.disabled = locked || !canUseWheelAdSpin(this.state, now);
    if (adBadge) {
      adBadge.classList.add("hidden");
      adBadge.setAttribute("aria-hidden", "true");
    }
  }
  if (skipBtn) {
    var adsOffered = canOfferRetentionRewardedAds();
    var freeReady = isFreeWheelSpinReady(this.state, now);
    var showClose = !freeReady && !adsOffered;
    skipBtn.textContent = showClose ? "Close" : "No, thanks";
    skipBtn.setAttribute("aria-label", showClose ? "Close" : "Dismiss wheel offer");
    skipBtn.disabled = locked;
  }
};

RetentionManager.prototype.spinWheel = function (mode) {
  if (this._wheelActionLocked()) return;
  var now = Date.now();
  if (mode !== "free" && mode !== "ad") return;
  if (mode === "free" && !isFreeWheelSpinReady(this.state, now)) return;
  if (mode === "ad" && !canUseWheelAdSpin(this.state, now)) return;

  var self = this;
  var runSpin = function () {
    var segmentIndex = pickWheelSegmentIndex();
    var grant = getWheelGrantForSegment(segmentIndex);
    var segment = RETENTION_WHEEL_SEGMENTS[segmentIndex];
    self._wheelSpunSinceOpen = true;
    self._trackRetentionAnalytics(
      mode === "ad" ? AnalyticsEvents.WHEEL_SPIN_AD : AnalyticsEvents.WHEEL_SPIN_FREE,
      { wheel_segment_id: segment && segment.id ? segment.id : "" }
    );
    self._resetWheelRewardFlow();
    self._wheelSpinning = true;
    self._stopWheelIdle();
    self._playWheelSpinSound();
    self._updateWheelButtons(now);
    self._animateWheelToSegment(segmentIndex, function () {
      var marked = markWheelSpinUsed(self.state, mode, Date.now());
      self.state = marked.state;
      self.save();
      self._setWheelWinnerHighlight(segmentIndex);
      self._wheelPendingClaim = { grant: grant, segmentIndex: segmentIndex };
      self._updateWheelButtons(Date.now());
      self._scheduleWheelRewardReveal(self._wheelPendingClaim);
    });
  };

  if (mode === "ad") {
    var game = this.game;
    // Lock the wheel while the ad is up so a second tap cannot request another one.
    this._wheelAdPending = true;
    this._trackPokiAction("wheel_ad_requested");
    this._updateWheelButtons(now);
    requestRetentionRewardedAd({
      name: REWARD_AD_NAME_WHEEL_SPIN,
      onStarted: function () {
        if (game && typeof game.setAdPaused === "function") game.setAdPaused(true);
      },
      onFinished: function () {
        if (game && typeof game.setAdPaused === "function") game.setAdPaused(false);
      },
      onError: function () {
        if (game && typeof game.setAdPaused === "function") game.setAdPaused(false);
      },
    }).then(function (result) {
      self._wheelAdPending = false;
      if (isRewardedAdGranted(result)) runSpin();
      else {
        self._updateWheelButtons(Date.now());
        self._toast(isRewardedAdCancelled(result) ? "Ad cancelled" : "Ad unavailable");
      }
    }, function () {
      self._wheelAdPending = false;
      if (game && typeof game.setAdPaused === "function") game.setAdPaused(false);
      self._updateWheelButtons(Date.now());
      self._toast("Ad unavailable");
    });
    return;
  }
  runSpin();
};

RetentionManager.prototype._animateWheelToSegment = function (segmentIndex, done) {
  var rotator = this.dom.wheelRotator;
  if (!rotator) {
    if (done) done();
    return;
  }
  var segmentAngle = 360 / RETENTION_WHEEL_SEGMENTS.length;
  var targetCenter = segmentIndex * segmentAngle + segmentAngle / 2;
  var extraTurns = 4 + Math.floor(Math.random() * 3);
  var current = ((this._wheelRotationDeg % 360) + 360) % 360;
  var alignTarget = (360 - targetCenter) % 360;
  var delta = extraTurns * 360 + ((alignTarget - current + 360) % 360);
  var final = this._wheelRotationDeg + delta;
  rotator.style.transition = "transform " + (WHEEL_SPIN_ANIMATION_MS / 1000) + "s " + WHEEL_SPIN_EASING;
  rotator.style.transform = "rotate(" + final + "deg)";
  this._wheelRotationDeg = final;
  var finished = false;
  var finish = function () {
    if (finished) return;
    finished = true;
    rotator.removeEventListener("transitionend", finish);
    if (done) done();
  };
  rotator.addEventListener("transitionend", finish);
  setTimeout(finish, WHEEL_SPIN_FALLBACK_MS);
};

RetentionManager.prototype.openGemsAd = function () {
  this._pauseForOverlay();
  this._updateGemsAdButton(Date.now());
  this._showOverlay(this.dom.gemsAdOverlay);
  this._startPopupTick();
  this._trackRetentionAnalytics(AnalyticsEvents.GEMS_AD_OPENED);
};

RetentionManager.prototype.closeGemsAd = function () {
  var self = this;
  this._hideOverlay(this.dom.gemsAdOverlay, function () {
    self._resumeFromOverlay();
  });
};

RetentionManager.prototype._updateGemsAdButton = function (now) {
  var btn = this.dom.gemsAdWatchBtn;
  if (!btn) return;
  var ready = canUseGemsAd(this.state, now);
  var remain = getCooldownRemainingMs(this.state.gemsAdLastAt, RETENTION_GEMS_AD_COOLDOWN_MS, now);
  var badge = this.dom.gemsAdCooldown;
  btn.disabled = !ready;
  if (badge) {
    setCooldownBadgeLabel(badge, remain);
    badge.classList.toggle("hidden", ready);
    badge.setAttribute("aria-hidden", ready ? "true" : "false");
  }
  var skipBtn = this.dom.gemsAdSkipBtn;
  if (skipBtn) {
    skipBtn.textContent = ready ? "No, thanks" : "Close";
    skipBtn.setAttribute("aria-label", ready ? "Dismiss free gems offer" : "Close");
  }
};

RetentionManager.prototype.watchGemsAd = function () {
  if (!canUseGemsAd(this.state, Date.now())) return;
  var self = this;
  var game = this.game;
  requestRetentionRewardedAd({
    name: REWARD_AD_NAME_EXTRA_GEMS,
    onStarted: function () {
      if (game && typeof game.setAdPaused === "function") game.setAdPaused(true);
    },
    onFinished: function () {
      if (game && typeof game.setAdPaused === "function") game.setAdPaused(false);
    },
    onError: function () {
      if (game && typeof game.setAdPaused === "function") game.setAdPaused(false);
    },
  }).then(function (result) {
    if (!isRewardedAdGranted(result)) {
      self._toast(isRewardedAdCancelled(result) ? "Ad cancelled" : "Ad unavailable");
      return;
    }
    self.state = markGemsAdUsed(self.state, Date.now());
    self.save();
    self._updateGemsAdButton(Date.now());
    self._openRewardReveal({ gems: RETENTION_GEMS_AD_REWARD }, function () {
      self._trackRetentionAnalytics(AnalyticsEvents.GEMS_AD_COMPLETED, {
        gems_granted: RETENTION_GEMS_AD_REWARD,
      });
      self._applyGrant({ gems: RETENTION_GEMS_AD_REWARD });
      self.syncHud();
      self.closeGemsAd();
    });
  });
};